// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getProp, resolveColor } from "@insite/mobius/utilities";
import { css } from "styled-components";

/**
 * Add custom style for scrollbar
 * @param width
 * @param verticalPadding
 * @param margin
 */
export const styledScrollbar = (width = 5, verticalPadding = 10, margin = 0) => {
    const backgroundColor = "common.ui2";
    const thumbColor = "common.ui9";
    const thumbHoverColor = "common.ui6";
    return ({ theme }: any) => css`
        overflow-y: auto;
        transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;

        @supports not selector(::-webkit-scrollbar) {
            scrollbar-width: thin;
            scrollbar-color: ${resolveColor(thumbColor, theme)} ${resolveColor(backgroundColor, theme)};
        }

        &::-webkit-scrollbar {
            width: ${width}px;
            height: ${width}px;
        }
        &::-webkit-scrollbar-track {
            background: ${resolveColor(backgroundColor, theme)};
        }
        &::-webkit-scrollbar-thumb {
            background-color: ${resolveColor(thumbColor, theme)};
            border-radius: 6px;
            border: 3px solid transparent;
        }
        &::-webkit-scrollbar-thumb:hover {
            background: ${resolveColor(thumbHoverColor, theme)};
        }
        // webkit-scrollbar-button to add scrollbar padding from top and bottom
        &::-webkit-scrollbar-button {
            ${verticalPadding && `padding:  ${verticalPadding}px 0;`}
        }

        &::-webkit-scrollbar-track {
            ${margin && `margin-block:  ${margin}px 0;`}
        }
    `;
};

export const hideScrollBar = css`
    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    /* this prevent scroll outside the container */
    overscroll-behavior-y: contain;
`;

const hasVScroll = () => document.body.scrollHeight > document.body.clientHeight;

export const addScrollbarGutter = () => {
    if (typeof document === "undefined") {
        return;
    }

    if (!hasVScroll()) {
        return;
    }

    document.querySelector("html")?.setAttribute("style", "scrollbar-gutter: stable;");
};

export const removeScrollbarGutter = () => {
    if (typeof document === "undefined") {
        return;
    }

    if (!hasVScroll()) {
        return;
    }

    document.querySelector("html")?.removeAttribute("style");
};

/**
 * Scroll to invalid field
 * @param scrollToTop
 */
export const doScroll = (scrollToTop = false) => {
    setTimeout(
        () => {
            const selector = document.querySelector("[aria-invalid='true']");

            if (selector) {
                selector.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });

                return;
            }

            if (scrollToTop) {
                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: "smooth",
                });
                return;
            }

            doScroll(scrollToTop);
        },
        scrollToTop ? 10 : 500,
    );
};
